import type { Editor, Range } from '@tiptap/core';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import type { MentionSuggestion } from '@tourfold/libraries/tf-api-client-public-v2';
import { CommentsService } from '../services/comments.service';

/**
 * Translatable strings the suggestion popover renders. Provided by the caller so the bare-DOM
 * popover stays decoupled from `@ngx-translate/core` (it can't easily reach Angular's DI from
 * inside a ProseMirror plugin lifecycle).
 */
export interface MentionSuggestionMessages {
  /** Shown when the query returns no matching targets. */
  readonly noMatches: string;
}

/**
 * The suggestion popover for the Tiptap `Mention` extension. Bare-DOM implementation rather than
 * an Angular component so the popover doesn't need a hosting ViewContainerRef inside Tiptap's
 * render lifecycle — Tiptap calls our hooks from inside a ProseMirror plugin, where Angular's
 * component-creation surface isn't easily reachable.
 *
 * The list:
 *  - Anchors to the typed `@` via Tiptap's `clientRect` callback (CSS `position: absolute`).
 *  - Highlights an `activeIndex`; ↑/↓ move it, Enter selects, Esc dismisses.
 *  - Click selects.
 *  - Polls the backend through {@link CommentsService.findMentionTargets} on every keystroke;
 *    Tiptap's suggestion plugin already debounces by virtue of the slow keystroke loop, so we
 *    don't add additional debouncing here.
 */
export function buildMentionSuggestion(
  commentsService: CommentsService,
  messages: MentionSuggestionMessages,
): Omit<SuggestionOptions<MentionSuggestion>, 'editor'> {
  return {
    char: '@',
    allowSpaces: false,
    items: async ({ query }: { query: string }) =>
      commentsService.findMentionTargets({ q: query, type: 'user', limit: 8 }),
    command: ({
      editor,
      range,
      props,
    }: {
      editor: Editor;
      range: Range;
      props: MentionSuggestion;
    }) => {
      // Replace the matched `@query` range with a mention node. The `Mention` extension's node
      // schema accepts `id` + `label`; we store `type` in a third attribute via the extension
      // configuration (see `rich-editor.component.ts`).
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'mention',
            attrs: {
              id: props.id,
              label: props.displayName,
              type: props.type,
            },
          },
          // A trailing space after the chip so the user can continue typing without a manual gap.
          { type: 'text', text: ' ' },
        ])
        .run();
    },
    render: () => {
      let listEl: HTMLDivElement | null = null;
      let items: MentionSuggestion[] = [];
      let activeIndex = 0;
      let onSelect: ((index: number) => void) | null = null;

      function render(): void {
        if (!listEl) return;
        listEl.innerHTML = '';
        if (items.length === 0) {
          const empty = globalThis.document.createElement('div');
          empty.className = 'tf-mention-suggestion__empty';
          empty.textContent = messages.noMatches;
          listEl.append(empty);
          return;
        }
        items.forEach((item, index) => {
          const row = globalThis.document.createElement('button');
          row.type = 'button';
          row.className = 'tf-mention-suggestion__item';
          if (index === activeIndex) row.classList.add('tf-mention-suggestion__item--active');
          row.dataset['index'] = String(index);

          const primary = globalThis.document.createElement('span');
          primary.className = 'tf-mention-suggestion__primary';
          primary.textContent = item.displayName;
          row.append(primary);

          if (item.secondaryText) {
            const secondary = globalThis.document.createElement('span');
            secondary.className = 'tf-mention-suggestion__secondary';
            secondary.textContent = item.secondaryText;
            row.append(secondary);
          }

          // Tiptap fires `onExit` on blur; using `mousedown` instead of `click` lets the
          // selection commit before the popup tears down.
          row.addEventListener('mousedown', (event) => {
            event.preventDefault();
            onSelect?.(index);
          });
          listEl?.append(row);
        });
      }

      function position(rect: DOMRect): void {
        if (!listEl) return;
        // The popup sits below the caret; flip above when there's no room.
        const padding = 4;
        const popHeight = listEl.offsetHeight;
        const viewportH = globalThis.innerHeight;
        const showBelow = rect.bottom + popHeight + padding <= viewportH;
        const top = showBelow ? rect.bottom + padding : rect.top - popHeight - padding;
        listEl.style.top = `${Math.max(top, padding)}px`;
        listEl.style.left = `${rect.left}px`;
      }

      return {
        onStart: (props: SuggestionProps<MentionSuggestion>) => {
          items = props.items;
          activeIndex = 0;
          onSelect = (index) => {
            const item = items[index];
            if (item) props.command(item);
          };
          listEl = globalThis.document.createElement('div');
          listEl.className = 'tf-mention-suggestion';
          listEl.style.position = 'absolute';
          listEl.style.zIndex = '10000';
          globalThis.document.body.append(listEl);
          render();
          const rect = props.clientRect?.();
          if (rect) position(rect);
        },
        onUpdate: (props: SuggestionProps<MentionSuggestion>) => {
          items = props.items;
          activeIndex = Math.min(activeIndex, Math.max(items.length - 1, 0));
          onSelect = (index) => {
            const item = items[index];
            if (item) props.command(item);
          };
          render();
          const rect = props.clientRect?.();
          if (rect) position(rect);
        },
        onKeyDown: (props: { event: KeyboardEvent }) => {
          if (props.event.key === 'ArrowDown') {
            activeIndex = (activeIndex + 1) % Math.max(items.length, 1);
            render();
            return true;
          }
          if (props.event.key === 'ArrowUp') {
            activeIndex = (activeIndex - 1 + items.length) % Math.max(items.length, 1);
            render();
            return true;
          }
          if (props.event.key === 'Enter') {
            onSelect?.(activeIndex);
            return true;
          }
          if (props.event.key === 'Escape') {
            return true;
          }
          return false;
        },
        onExit: () => {
          listEl?.remove();
          listEl = null;
          onSelect = null;
          items = [];
        },
      };
    },
  };
}
